import React, { useEffect, useMemo, useState } from 'react';
import { Category, LearningAsset, Tag } from '../../../../../model/response';
import { findLearningAssetById, updateLearningAssetFromTree } from '../../../../../service/learningAssetService';
import { LearningAssetStatus, LearningAssetTreeRequest } from '../../../../../model/request';
import { Button, Image, Input, message, Select } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { learningAssetStatusList } from '../../../../../utils/enumMapper';
import processError from '../../../../../utils/processError';

interface LearningAssetEditViewProps {
  learningAsset: LearningAsset;
  tagFetching: boolean;
  tags: Tag[];
  fetchTags: () => void;
  categoryFetching: boolean;
  categories: Category[];
  fetchCategories: () => void;
}

function LearningAssetEditView({
  learningAsset,
  tagFetching,
  tags,
  fetchTags,
  categoryFetching,
  categories,
  fetchCategories,
}: LearningAssetEditViewProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [chapter, setChapter] = useState('');
  const [lesson, setLesson] = useState('');
  const [status, setStatus] = useState<LearningAssetStatus | null>('ACTIVE');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [savedFilePath, setSavedFilePath] = useState<string | null>(null);
  const [savedOriginFilename, setSavedOriginFilename] = useState<string | null>(null);
  const [saveFetching, setSaveFetching] = useState(false);
  const [saved, setSaved] = useState(false);

  const id = useMemo(() => {
    return learningAsset.id;
  }, [learningAsset]);

  useEffect(() => {
    findLearningAssetById(id)
      .then((learningAsset) => {
        setName(learningAsset.name);
        setDescription(learningAsset.description || '');
        setChapter(learningAsset.chapter || '');
        setLesson(learningAsset.lesson || '');
        setStatus(learningAsset.status || null);
        setTagIds(learningAsset.tags.map((tag) => tag.id));
        setCategoryIds(learningAsset.categories.map((category) => category.id));
        setSavedFilePath(learningAsset.filePath);
        setSavedOriginFilename(learningAsset.originFilename);
      })
      .catch((e) => console.error(e));
  }, [id]);

  async function handleSubmit() {
    try {
      setSaveFetching(true);
      const learningAssetUpdateRequest: LearningAssetTreeRequest = {
        name,
        description,
        chapter,
        lesson,
        status: status || undefined,
        categories: categoryIds.map((categoryId) => ({ id: categoryId })),
        tags: tagIds.map((tagId) => ({ id: tagId })),
      };
      await updateLearningAssetFromTree(id, learningAssetUpdateRequest);
      setSaved(true);
      message.success('Cập nhật học liệu số thành công');
    } catch (err) {
      processError(err);
    } finally {
      setSaveFetching(false);
    }
  }

  return (
    <div className={'p-4 rounded bg-F5F5F5 ' + (saved ? '!bg-[#e6f8ff]' : '')}>
      <div className={'w-full mb-4'}>
        {savedFilePath ? (
          <div className="flex items-center">
            <div className="text-[18px] w-[20%] text-left">Ảnh:</div>
            <div className={'flex flex-col gap-y-2 justify-start'}>
              <div>
                <Image
                  className={'rounded border-1'}
                  width={'auto'}
                  height={180}
                  alt={savedOriginFilename || id}
                  src={savedFilePath}
                />
              </div>
            </div>
          </div>
        ) : null}
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Thẻ:</div>
          <Select
            mode="multiple"
            onChange={setTagIds}
            placeholder="Chọn thẻ"
            style={{ flex: 1 }}
            value={tagIds}
            options={tags.map((tag) => ({
              label: tag.name,
              value: tag.id,
            }))}
            filterOption={(input, option) => !!option?.label?.toLowerCase()?.includes(input.toLowerCase())}
          />
          <Button className={'ml-2'} onClick={fetchTags}>
            {tagFetching ? <LoadingOutlined /> : <ReloadOutlined />}
          </Button>
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Chủ đề:</div>
          <Select
            mode="multiple"
            onChange={setCategoryIds}
            placeholder="Chọn chủ đề"
            style={{ flex: 1 }}
            value={categoryIds}
            options={categories.map((tag) => ({
              label: tag.name,
              value: tag.id,
            }))}
            filterOption={(input, option) => !!option?.label?.toLowerCase()?.includes(input.toLowerCase())}
          />
          <Button className={'ml-2'} onClick={fetchCategories}>
            {categoryFetching ? <LoadingOutlined /> : <ReloadOutlined />}
          </Button>
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Tên học liệu số:</div>
          <Input className="flex-1" name={'name'} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Mô tả chi tiết:</div>
          <TextArea
            className="flex-1"
            name={'description'}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
          />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Chương:</div>
          <Input className="flex-1" name={'chapter'} value={chapter} onChange={(e) => setChapter(e.target.value)} />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Bài:</div>
          <Input className="flex-1" name={'chapter'} value={lesson} onChange={(e) => setLesson(e.target.value)} />
        </div>
        <div className="flex items-center mt-2">
          <div className="text-[18px] w-[20%] text-left">Trạng thái:</div>
          <Select
            onChange={(value) => setStatus(value)}
            className="w-full"
            placeholder="Trạng thái"
            style={{ flex: 1 }}
            value={status}
            options={learningAssetStatusList}
          />
        </div>
      </div>
      <div>
        <Button type={'primary'} onClick={handleSubmit} loading={saveFetching}>
          {saved ? 'Cập nhật lại' : 'Cập nhật'}
        </Button>
      </div>
    </div>
  );
}

export default LearningAssetEditView;
